import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

/**
 * Composable for managing conversation labels.
 *
 * Two kinds of labels are handled transparently here:
 *  - public labels  -> shared account-wide, stored via tags (conversationLabels)
 *  - private labels  -> personal to the current user (conversationPrivateLabels)
 *
 * Both are matched by title. Titles are guaranteed unique within a single
 * user's view (all public labels + their own private labels), so a title maps
 * to exactly one label object in `accountLabels`, which lets us route each
 * add/remove to the correct subsystem based on its `label_type`.
 *
 * @returns {Object} An object containing methods and computed properties for conversation labels
 */
export function useConversationLabels() {
  const store = useStore();
  const getters = useStoreGetters();

  /**
   * The currently selected chat
   * @type {import('vue').ComputedRef<Object>}
   */
  const currentChat = computed(() => getters.getSelectedChat.value);

  /**
   * The ID of the current conversation
   * @type {import('vue').ComputedRef<number|null>}
   */
  const conversationId = computed(() => currentChat.value?.id);

  /**
   * All labels available for the account (public + the current user's private)
   * @type {import('vue').ComputedRef<Array>}
   */
  const accountLabels = computed(
    () => getters['labels/getLabelsWithPrivate'].value
  );

  /**
   * Public label titles currently saved to the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const savedPublicLabels = computed(() => {
    return store.getters['conversationLabels/getConversationLabels'](
      conversationId.value
    );
  });

  /**
   * The current user's private label titles currently saved to the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const savedPrivateLabels = computed(() => {
    return store.getters[
      'conversationPrivateLabels/getConversationPrivateLabels'
    ](conversationId.value);
  });

  /**
   * All label titles (public + private) currently saved to the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const savedLabels = computed(() => [
    ...savedPublicLabels.value,
    ...savedPrivateLabels.value,
  ]);

  /**
   * Labels currently active on the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const activeLabels = computed(() =>
    accountLabels.value.filter(({ title }) => savedLabels.value.includes(title))
  );

  /**
   * Labels available but not active on the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const inactiveLabels = computed(() =>
    accountLabels.value.filter(
      ({ title }) => !savedLabels.value.includes(title)
    )
  );

  const isPrivateLabel = title => {
    const label = accountLabels.value.find(item => item.title === title);
    return label?.label_type === 'private';
  };

  const activePublicLabelTitles = computed(() =>
    activeLabels.value
      .filter(label => label.label_type !== 'private')
      .map(label => label.title)
  );

  const activePrivateLabelTitles = computed(() =>
    activeLabels.value
      .filter(label => label.label_type === 'private')
      .map(label => label.title)
  );

  /**
   * Updates the public labels for the current conversation
   * @param {string[]} selectedLabels - Array of public label titles
   * @returns {Promise<void>}
   */
  const onUpdateLabels = async selectedLabels => {
    await store.dispatch('conversationLabels/update', {
      conversationId: conversationId.value,
      labels: selectedLabels,
    });
  };

  /**
   * Updates the current user's private labels for the current conversation
   * @param {string[]} selectedLabels - Array of private label titles
   * @returns {Promise<void>}
   */
  const onUpdatePrivateLabels = async selectedLabels => {
    await store.dispatch('conversationPrivateLabels/update', {
      conversationId: conversationId.value,
      labels: selectedLabels,
    });
  };

  /**
   * Adds a label to the current conversation, routing to the public or private
   * subsystem based on the label's visibility.
   * @param {Object} value - The label to add (at least a `title`)
   * @param {string} value.title - The title of the label to be added
   * @param {string} [value.label_type] - 'public' | 'private' (resolved from
   *   accountLabels when missing)
   */
  const addLabelToConversation = value => {
    const labelType =
      value.label_type ??
      accountLabels.value.find(item => item.title === value.title)?.label_type;

    if (labelType === 'private') {
      onUpdatePrivateLabels([...activePrivateLabelTitles.value, value.title]);
    } else {
      onUpdateLabels([...activePublicLabelTitles.value, value.title]);
    }
  };

  /**
   * Removes a label from the current conversation
   * @param {string} value - The title of the label to be removed
   */
  const removeLabelFromConversation = value => {
    if (isPrivateLabel(value)) {
      onUpdatePrivateLabels(
        activePrivateLabelTitles.value.filter(title => title !== value)
      );
    } else {
      onUpdateLabels(
        activePublicLabelTitles.value.filter(title => title !== value)
      );
    }
  };

  return {
    accountLabels,
    savedLabels,
    activeLabels,
    inactiveLabels,
    addLabelToConversation,
    removeLabelFromConversation,
    onUpdateLabels,
    onUpdatePrivateLabels,
  };
}
